import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { ButtonStyle } from '../components/Button';
import Page from '../components/Page';
import Section from '../components/Section';
import { Box } from '../components/Box';
import { Text, TextArea, CountrySelect } from '../components/forms';
import NewCompanyFeatureList from '../components/NewCompanyFeatureList';
import { ActionBar, ActionBarStyle } from '../components/ActionBar';
import { useRequestAsync, getHeaders } from '../api/client';
import { ProfileManager, ProfileContext } from '../profile';
import { ErrorFlash, SuccessFlash } from '../components/Flash';

interface Props extends RouteComponentProps<any> {}

type NewCompanyParams = {
  name: string;
  description_text: string;
  contact_email: string;
  public_url: string;
  location: {
    country: string;
  };
};

const newCompanyParams: NewCompanyParams = {
  name: null,
  description_text: null,
  contact_email: null,
  public_url: null,
  location: {
    country: null,
  },
};

const setValue = (obj: any, onChange: Function) => (
  field: string,
  newValue: String,
) =>
  onChange(
    Object.assign({}, obj, {
      [field]: newValue,
    }),
  );

const setLocationValue = (obj: any, onChange: Function) => (
  field: string,
  newValue: String,
) =>
  onChange(
    Object.assign({}, obj, {
      location: {
        ...obj.location,
        [field]: newValue,
      },
    }),
  );

const create = (context: ProfileManager) =>
  useRequestAsync((data: NewCompanyParams) => ({
    url: '/companies/new',
    method: 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

export default function NewCompany(props: Props) {
  const context = React.useContext(ProfileContext);
  const { profile } = context;
  const [company, setCompany] = React.useState<NewCompanyParams>(
    newCompanyParams,
  );
  const [state, submit] = create(context);

  React.useEffect(() => {
    if (!state.data && !state.complete) {
      return;
    }

    // Merge the profile…
    context.set({
      ...profile,
      company_id: state.data.id,
      company: state.data,
    });

    props.history.push('/company');
  }, [state]);

  return (
    <Page
      title="Sign up to Coffee Brands"
      subtitle="Get your coffee in front of Filtru users"
    >
      {state.error && <ErrorFlash error={state.error} />}
      {!state.error && state.complete && (
        <SuccessFlash text="Company has been saved" />
      )}
      <Section title="Basic Details">
        <div className="flex">
          <div className="flex-auto w-50 pr2">
            <Text
              label="Company Name"
              field={['name']}
              id="name"
              defaultValue={company.name}
              onValueChange={setValue(company, setCompany)}
            />
            <Text
              label="Contact Email"
              field={['contact_email']}
              id="contact_email"
              defaultValue={company.contact_email}
              onValueChange={setValue(company, setCompany)}
            />
            <CountrySelect
              label="Country"
              field={['country']}
              id="country"
              defaultValue={company.location.country}
              onValueChange={setLocationValue(company, setCompany)}
            />
          </div>
          <div className="flex-auto w-50 pl2">
            <Text
              label="Website URL"
              field={['public_url']}
              id="public_url"
              defaultValue={company.public_url}
              onValueChange={setValue(company, setCompany)}
            />
            <TextArea
              label="Company Description"
              field={['description_text']}
              id="description_text"
              defaultValue={company.description_text}
              onValueChange={setValue(company, setCompany)}
            />
          </div>
        </div>
      </Section>
      <ActionBar
        className={ActionBarStyle.alignCenter}
        actions={[
          {
            className: ButtonStyle.Confirm,
            label: 'Create',
            handler: () => submit(company),
          },
        ]}
      />
      <NewCompanyFeatureList />
    </Page>
  );
}
